"use client";

import type { LinkTarget } from "@/components/link";
import { ALBUM_TYPES } from "@garimpa/lib/albumTypes";
import type { Filter } from "@garimpa/lib/facetFilter";
import { FORMAT_CATEGORIES } from "@garimpa/lib/formatGroups";
import type { ReleaseSearchFilterKey } from "@garimpa/lib/searchState";

import { SearchCountryFilters } from "./search-country-filters";
import { searchFacetValues } from "./search-facet-values";
import { SearchOrganizationFilters } from "./search-organization-filters";
import { SearchTaxonomyFilters } from "./search-taxonomy-filters";
import { SearchYearFilters } from "./search-year-filters";

interface FacetCount {
  value: string;
  count: number;
}

export interface SearchFacets {
  typeCounts: Record<string, number>;
  formatCounts: Record<string, number>;
  genreCounts: FacetCount[];
  styleCounts: FacetCount[];
  labelCounts: FacetCount[];
  companyCounts: FacetCount[];
  countryCounts: FacetCount[];
  artistCountryCounts: FacetCount[];
  yearCounts: FacetCount[];
  totalCount: number;
}

export interface SearchFiltersProps {
  genre: string | undefined;
  style: string | undefined;
  type: (typeof ALBUM_TYPES)[number] | undefined;
  format: (typeof FORMAT_CATEGORIES)[number] | undefined;
  country: string | undefined;
  origin: string | undefined;
  yearFrom: number | undefined;
  yearTo: number | undefined;
  label: string[];
  onLabelChange: (labels: string[]) => void;
  company: string[];
  filter: Filter | undefined;
  onCompanyChange: (companies: string[]) => void;
  facets: SearchFacets | null;
  updateFilter: (key: string, value: string | number | boolean | undefined) => void;
  updateFilters: (updates: Record<string, string | number | undefined>) => void;
  getFacetLinkTarget?: (key: ReleaseSearchFilterKey, value: string) => LinkTarget;
}

export function SearchFilters(props: SearchFiltersProps) {
  const facets = searchFacetValues(props.facets);
  return (
    <div className="flex flex-col gap-4">
      <SearchTaxonomyFilters
        genre={props.genre}
        style={props.style}
        type={props.type}
        format={props.format}
        genreCounts={facets.genreCounts}
        styleCounts={facets.styleCounts}
        typeCounts={facets.typeCounts}
        formatCounts={facets.formatCounts}
        filter={props.filter}
        updateFilter={props.updateFilter}
        updateFilters={props.updateFilters}
        getFacetLinkTarget={props.getFacetLinkTarget}
      />
      <SearchCountryFilters
        music
        country={props.country}
        origin={props.origin}
        countryCounts={facets.countryCounts}
        artistCountryCounts={facets.artistCountryCounts}
        updateFilter={props.updateFilter}
      />
      <SearchYearFilters
        yearFrom={props.yearFrom}
        yearTo={props.yearTo}
        yearCounts={facets.yearCounts}
        updateFilter={props.updateFilter}
        updateFilters={props.updateFilters}
      />
      <SearchOrganizationFilters
        label={props.label}
        company={props.company}
        labelCounts={facets.labelCounts}
        companyCounts={facets.companyCounts}
        filter={props.filter}
        onLabelChange={props.onLabelChange}
        onCompanyChange={props.onCompanyChange}
      />
    </div>
  );
}
